/**
 * PublicRelatedPanel — another table's data, shown around the records.
 *
 * A public list page can pull in tables besides its own. Until this existed the
 * only way to DISPLAY that data was to write a custom layout, which meant
 * hand-writing the card markup and CSS as well — so wanting "the standard cards,
 * plus a list of courses above them" was not expressible at all.
 *
 * NOTHING RENDERS UNLESS IT WAS ASKED FOR. Each source carries
 * `list_placement`, chosen in the admin, defaulting to 'none'. An earlier
 * version of this drew every page-level block automatically, which meant a
 * builder had no say over whether or where it appeared. Placement is the fix:
 * the builder decides, and the default is off, so existing pages are unchanged.
 *
 * Only PAGE-scoped sources can be placed. A source matched against the record
 * produces different rows per record, so it belongs inside a card — putting it
 * here would show one record's data as if it belonged to the whole page.
 *
 * Values render as text, never as markup, the same rule the record renderer
 * follows: a column holding "<b>x</b>" shows those characters.
 */

import React from 'react';

function label(alias) {
  const words = String(alias || '').replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Related';
}

function rowsOf(block) {
  const data = block && block.data;
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
}

/** The blocks the builder placed at `position`, in config order. */
export function blocksAt(related, position) {
  return Object.entries(related || {}).filter(([, block]) =>
    block
    && block.scope === 'page'
    && block.list_placement === position
    && block.status === 'ok'
    && rowsOf(block).length > 0
  );
}

export default function PublicRelatedPanel({ related, position, accentColor }) {
  const blocks = blocksAt(related, position);
  if (blocks.length === 0) return null;

  return (
    <div className="mb-4">
      {blocks.map(([alias, block]) => {
        const rows = rowsOf(block);
        const columns = Object.keys(rows[0] || {});

        return (
          <div
            key={alias}
            className="rounded p-3 mb-3"
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 'var(--card-radius, 12px)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}
          >
            <div
              className="fw-semibold mb-2"
              style={{ fontSize: '0.95rem', color: accentColor || 'inherit' }}
            >
              {label(alias)}
            </div>

            <div className="d-flex flex-wrap gap-2">
              {rows.map((row, index) => (
                <div
                  key={index}
                  className="px-2 py-1 rounded"
                  style={{ background: '#f8fafc', border: '1px solid #eef2f7', fontSize: '0.85rem' }}
                >
                  {columns.map((column, position_) => (
                    <span key={column}>
                      {position_ > 0 && <span className="text-muted"> · </span>}
                      <span className={position_ === 0 ? 'fw-semibold' : 'text-muted'}>
                        {row[column] === null || row[column] === undefined
                          ? '' : String(row[column])}
                      </span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
